import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../AppContext';

interface SideHustleLayoutProps {
  children: React.ReactNode;
  currentPage?: string;
}

const SideHustleLayout: React.FC<SideHustleLayoutProps> = ({ children, currentPage }) => {
  const navigate = useNavigate();
  const { signOut, userProfile } = useApp();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const navItems = [
    { id: 'talents', label: 'Talents & Passions', path: '/side-hustle/talents' },
    { id: 'problems', label: 'Problem Identifier', path: '/side-hustle/problems' },
    { id: 'canvas', label: 'Business Model Canvas', path: '/side-hustle/canvas' },
    { id: 'budgeting', label: 'Business Budgeting', path: '/side-hustle/budgeting' },
    { id: 'leap', label: 'Taking the Leap', path: '/side-hustle/leap' },
    { id: 'dreams', label: 'The Dream Station', path: '/side-hustle/dreams' },
  ];

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-secondary">
      {/* Header */}
      <header className="w-full bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <button
            onClick={() => navigate('/side-hustle/home')}
            className="text-2xl font-bold text-primary hover:text-primary/80 transition-colors"
          >
            Side Hustle Builder
          </button>

          <div className="flex items-center gap-4">
            {/* Navigation Toggle Button */}
            <button
              onClick={() => setDrawerOpen(!drawerOpen)}
              className="bg-primary text-white px-4 py-2 rounded-full hover:bg-primary/90 transition-all font-semibold"
            >
              {drawerOpen ? 'Close Menu' : 'Menu'}
            </button>

            {/* Profile / Logout */}
            {userProfile && (
              <div className="relative group">
                <button className="flex items-center gap-2 bg-gray-100 px-4 py-2 rounded-full hover:bg-gray-200 transition-all">
                  <div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center font-semibold">
                    {userProfile.firstName?.[0] || userProfile.email?.[0] || 'U'}{userProfile.lastName?.[0] || ''}
                  </div>
                  <span className="font-semibold text-gray-700">
                    {userProfile.firstName || userProfile.email?.split('@')[0] || 'User'}
                  </span>
                </button>

                {/* Dropdown */}
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                  <button
                    onClick={() => navigate('/')}
                    className="w-full text-left px-4 py-3 hover:bg-gray-100 rounded-t-lg font-semibold text-gray-700"
                  >
                    Back to Tools
                  </button>
                  <button
                    onClick={handleSignOut}
                    className="w-full text-left px-4 py-3 hover:bg-gray-100 rounded-b-lg font-semibold text-red-600"
                  >
                    Sign Out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-80 bg-white shadow-2xl transform transition-transform duration-300 z-50 ${
          drawerOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-primary">Components</h2>
            <button
              onClick={() => setDrawerOpen(false)}
              className="text-gray-500 hover:text-gray-700 text-2xl"
            >
              ×
            </button>
          </div>

          <nav className="space-y-3">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  navigate(item.path);
                  setDrawerOpen(false);
                }}
                className={`w-full text-left px-4 py-3 rounded-xl font-semibold transition-all ${
                  currentPage === item.id
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {item.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Overlay */}
      {drawerOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-40"
          onClick={() => setDrawerOpen(false)}
        />
      )}

      {/* Main Content */}
      <main>{children}</main>
    </div>
  );
};

export default SideHustleLayout;
